import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import { useNotify, useGetList } from 'react-admin';
import { useCreateLeadModal } from '../hooks/useCreateLeadModal';
import { LeadStatusChoices } from '../leads.model';
import { ALL_ORGANIZATIONS_RESOURCE_NAME as ORGANIZATIONS_RESOURCE } from '../../organizations/organizations.dataProvider';
import PhoneNumberInput from './PhoneNumberInput.component';
import StatesSelect from './StatesSelect.component';

const CreateLeadModal = ({ open, onClose, onSuccess }) => {
  const {
    formData,
    setFormData,
    isSubmitting,
    errors,
    validateField,
    validateForm,
    resetForm,
    handleSubmit,
  } = useCreateLeadModal(onSuccess, onClose);

  const notify = useNotify();

  // Fetch organizations list
  const { data: organizations, isLoading: organizationsLoading } = useGetList(
    ORGANIZATIONS_RESOURCE,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    },
  );

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only depend on open to avoid infinite loops

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      validateField(field, value);
    }
  }, [errors, validateField, setFormData]);

  const handleSubmitClick = useCallback(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      notify('Please fix the validation errors', { type: 'warning' });
      return;
    }

    await handleSubmit();
  }, [validateForm, handleSubmit, notify]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Memoize organization menu items to avoid re-rendering on every form update
  const organizationMenuItems = useMemo(
    () =>
      organizations?.map((org) => (
        <MenuItem key={org.id} value={org.id}>
          {org.name}
        </MenuItem>
      )),
    [organizations]
  );

  // Memoize error display to avoid re-rendering on every form update
  const errorDisplay = useMemo(() => {
    // Filter out entries with no error message
    const errorEntries = Object.entries(errors).filter(([, error]) => error && error.trim());

    if (errorEntries.length === 0) {
      return null;
    }

    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Please fix the following errors:
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          {errorEntries.map(([field, error]) => (
            <li key={field}>{String(error)}</li>
          ))}
        </ul>
      </Alert>
    );
  }, [errors]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Add New Lead
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create a new lead entry manually
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email || 'Required if phone number is not provided'}
              />
            </Grid>
          </Grid>


          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <PhoneNumberInput
                label="Phone Number"
                value={formData.phoneNumber || ''}
                onChange={(value) => handleFieldChange('phoneNumber', value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber || 'Required if email is not provided'}
                placeholder="Enter a US phone number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Language"
                value={formData.language || ''}
                onChange={(e) => handleFieldChange('language', e.target.value)}
                placeholder="e.g., English, Spanish"
              />
            </Grid>
          </Grid>

          {/* Address Information Section */}
          <Typography variant="h6" gutterBottom color="primary">
            Address Information
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ''}
                onChange={(e) => handleFieldChange('city', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <StatesSelect
                label="State"
                value={formData.state || ''}
                onChange={(value) => handleFieldChange('state', value)}
                error={!!errors.state}
                helperText={errors.state}
                required
                placeholder="Select or search state"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode || ''}
                onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                required
              />
            </Grid>
          </Grid>

          {/* Additional Information Section */}
          <Typography variant="h6" gutterBottom color="primary">
            Additional Information
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth *"
                type="date"
                value={formData.birthDate || ''}
                onChange={(e) => handleFieldChange('birthDate', e.target.value)}
                error={!!errors.birthDate}
                helperText={errors.birthDate || 'Person must be at least 16 years old'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WIC Clinic Code"
                value={formData.wicClinicCode || ''}
                onChange={(e) => handleFieldChange('wicClinicCode', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Source Organization"
                value={formData.sourceOrgId || ''}
                onChange={(e) => handleFieldChange('sourceOrgId', e.target.value)}
                disabled={organizationsLoading}
                error={!!errors.sourceOrgId}
                helperText={errors.sourceOrgId || (organizationsLoading ? 'Loading organizations...' : '')}
                required
              >
                {organizationMenuItems}
              </TextField>
            </Grid>
          </Grid>

          {/* SMS Marketing */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.smsOptIn || false}
                  onChange={(e) => handleFieldChange('smsOptIn', e.target.checked)}
                />
              }
              label="SMS Marketing"
            />
          </Box>

          {/* Error Display */}
          {errorDisplay}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmitClick}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Creating...' : 'Create Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeadModal;
